/* global Loot */
import { Tracker, Product } from '@studyportals/datalake-event-tracker';

(function () {
	// The user agent is used to check what browser the user is on (in DoD or not).
	const userAgent = navigator.userAgent.toLowerCase();

	/**
	 * Check whether a browser / device is mentioned in the user agent string.
	 *
	 * @param {string} str
	 * @returns {boolean}
	 */

	const is = function (str) {
		return userAgent.indexOf(str.toLowerCase()) !== -1;
	};

	// If the used browser is not in the DoD, no experiment functionality should be made available.
	if (
		!(
			/* Windows */
			(
				(is('windows') && (is('chrome') || is('firefox') || is('edge'))) ||
				/* Macintosh */
				(is('macintosh') && (is('chrome') || is('safari'))) ||
				/* Android */
				(is('android') && is('chrome')) ||
				/* iOS (iPhone) */
				(is('iphone') && is('safari'))
			)
		)
	) {
		// Browser not one of main DoD browsers, so stop here.
		return;
	}

	window.ExperimentScript = class {
		constructor(experimentName, variations, style) {
			this.experimentName = experimentName;
			this.variations = variations;
			this.style = document.createElement('style');
			this.style.innerHTML = style;
			this.variation = {};
			this.tracker = new Tracker(Product.EXPERIMENT);

			const validationError = this.experimentPropertiesValidation(experimentName, variations, style);

			if (validationError.length) {
				console.warn('Experiment.constructor: Experiment properties not of right format; ', validationError);
				return;
			}

			this.initiateExperiment();
		}

		/**
		 * Check whether the properties that are used to define the experiment are of
		 * the right format; and return the validation error that was found, if any.
		 *
		 * @param {string} experimentName
		 * @param {array} variations
		 * @param {string} style
		 * @returns {string}
		 */

		experimentPropertiesValidation(experimentName, variations, style) {
			let error = '';

			if (typeof experimentName !== 'string' || !experimentName.length) {
				error = 'No experiment name';
			}

			if (Object.prototype.toString.call(variations) !== '[object Array]' || !variations.length) {
				error = 'Variations not of right format';
			}

			let traffic = 0;

			variations.forEach((variation) => {
				if (
					typeof variation.number !== 'number' ||
					typeof variation.traffic !== 'number' ||
					typeof variation.name !== 'string' ||
					!variation.name.length
				) {
					error = 'One of variations not of right format';
					return;
				}

				traffic += variation.traffic;
			});

			if (Math.round(traffic) !== 100) {
				error = 'Total traffic of all variations is not 100%';
			}

			if (typeof style !== 'string') {
				error = 'Styling not of right format';
			}

			return error;
		}

		/**
		 * Check that any dependencies are available or that any assumptions (in terms
		 * of what the page should look like for the experiment to run properly) are
		 * correct. This function can be overwritten in the class that extends this
		 * boilerplate class to check for dependencies that are relevant to the experiment.
		 *
		 * @returns {boolean}
		 */

		areConditionsMet() {
			return true;
		}

		/**
		 * Set 'this.variation' either as [1] what was found in local storage, or
		 * [2] the variation that is selected by the 'this.decideVariation' function.
		 *
		 * @returns boolean
		 */

		setVariation() {
			if (
				!window.Loot ||
				!Loot.DataStorage ||
				typeof Loot.DataStorage.retrieve !== 'function' ||
				typeof Loot.DataStorage.store !== 'function'
			) {
				console.warn("Experiment.setVariation: Couldn't track data because snowplow is not available.", window.Loot);
				return false;
			}

			const storageKey = 'Experiment/' + this.experimentName;
			this.variation = Loot.DataStorage.retrieve(storageKey);

			if (!this.variation || typeof this.variation.number !== 'number') {
				this.decideVariation();
			}

			Loot.DataStorage.store(storageKey, this.variation, 60 * 60 * 24 * 30 * 3);
			return true;
		}

		/**
		 * Pick one of the variations randomly, while taking into account the share
		 * of traffic that each variation should get.
		 *
		 * @returns void
		 */

		decideVariation() {
			const self = this;
			const randomNumber = Math.random() * 100;
			let count = 0;

			this.variations.some((item) => {
				count += item.traffic;

				if (count > randomNumber) {
					self.variation = item;
					return true;
				}
			});
		}

		/**
		 * Fire tracking events to Redshift with a pre-defined experiment-specific 'action' and the 'label'
		 * and 'property' that are passed along.
		 *
		 * @param {string} label
		 * @param {string} property
		 * @param {number} value
		 * @returns void
		 */

		fireTracking(label, property = null, value = null, enableSnowplow = true, enableGTM = true) {
			setTimeout(() => {
				try {
					var trackingData = {
						category: Product.EXPERIMENT,
						action: this.experimentName + ' - ' + this.variation.number,
						label: label,
						property: property,
						value: value
					};

					this.tracker.trackExperimentEvent(trackingData, enableSnowplow, enableGTM);
				} catch (e) {
					if (typeof window.Rollbar !== 'undefined') {
						window.Rollbar.error('Issue with tracking experiment', e);
					} else {
						console.error('Issue with tracking experiment.', e);
					}
				}
			}, 100);
		}

		/**
		 * Any actions that should be taken that differentiate the variations from
		 * control. This function HAS to be overwritten in the class that extends this
		 * boilerplate class, because otherwise the variations are exactly the same
		 * as control.
		 *
		 * @returns void
		 */

		performActionsForVariations() {
			document.head.appendChild(this.style);

			// Depending on the variation, perform the needed changes.
		}

		/**
		 * Any actions that should be taken for control. Of course this shouldn't
		 * be any visual changes, because then control becomes a variation and that's
		 * not what we want. What could be useful for some experiments is to add
		 * tracking to certain elements to more easily compare control with the
		 * variations. In that case this function can be overwritten in the class
		 * that extends this boilerplate class.
		 *
		 * @returns void
		 */

		performActionsForControl() {}

		/**
		 * See whether all the criteria are met to trigger the experiment.
		 *
		 * @returns void
		 */

		initiateExperiment() {
			// Use separate variable so that the 'console.info' message further below is not stripped out with minification.
			const prdAvailableConsole = console;

			if (!this.setVariation()) {
				return;
			}

			if (typeof this.variation.number !== 'number' || !this.variation.name) {
				console.warn('Experiment.seeIfExperimentCanBeTriggered: variation not set properly.', this.variation);

				return;
			}

			if (!this.areConditionsMet()) {
				return;
			}

			// Log which variation is running.
			prdAvailableConsole.info(
				'Experiment running: ' + `${this.experimentName} on variation ${this.variation.number} ${this.variation.name}.`
			);

			// Call different functionality for control than all the variations.
			if (this.variation.number === 0) {
				this.performActionsForControl();
			} else {
				this.performActionsForVariations();
			}

			// Add experiment to the global variable that is used to track all experiments.
			if (sessionStorage.getItem('sp_active_experiments') === null)
				sessionStorage.setItem('sp_active_experiments', '[]');

			const experiment = {
				name: this.experimentName,
				variation: {
					id: this.variation.number,
					name: this.variation.name,
					traffic: this.variation.traffic
				}
			};

			var active_experiments = JSON.parse(sessionStorage.getItem('sp_active_experiments'));
			if (!active_experiments.some((e) => e.name === experiment.name && e.variation.id === experiment.variation.id)) {
				active_experiments.push(experiment);
				sessionStorage.setItem('sp_active_experiments', JSON.stringify(active_experiments));
			}

			// Dispatch event to let other scripts know that an experiment has been initiated.
			document.dispatchEvent(new CustomEvent('dataLayer-sp-experiment-initiated'));

			this.fireTracking('ExperimentImpression', null, null, true, false);
		}
	};

	// Function that can be used to easily extend the ExperimentScript class in an ES5-safe way
	window.addExperiment = function (experimentName, variations, style, methods) {
		class experiment extends window.ExperimentScript {
			constructor() {
				super(experimentName, variations, style);
			}
		}

		// Set up class functions for each method that is passed along.
		methods.forEach((method) => {
			if (typeof method.name !== 'string' || typeof method.code !== 'function') {
				console.warn('window.addExperiment: method not of expected format.', method);
				return;
			}

			experiment.prototype[method.name] = method.code;
		});

		// Create an instance of the class so that the experiment is run.
		new experiment();
	};
})();
